import React, { Fragment, useCallback, useState } from 'react';
import {
  CohortSampleOperationKey,
  ICohortFieldsFragment,
  QueryType,
  ScheduleType,
} from '../../../../utils/apollo/graphqlSchema';
import * as BuilderUI from '../../../../components/Builder/BuilderUI';
import { CohortRow } from '../../../../components/Builder/Cohorts';
import { BuilderAddRowAction } from '../../../../components/Builder/BuilderActions';
import DndDroppableZone from '../../../../components/DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../../../components/QuickAccessDictionary/DictionaryTabs/types';
import {
  EntityDropzoneDataTypeEnum,
  ICohortEntityDropzoneDataType,
  IEventEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../../../components/DndEntity/validation';
import DropzoneArea from '../../../../components/DndEntity/DndDroppableZone/DropzoneArea';
import { ReroutingEvents } from '../../../../components/Builder/BuilderFields';
import Scheduler from '../../../../components/Scheduler';
import { SaveCohortModal } from '../../../../components/Cohort';
import { useDndEntityInternalContext } from '../../../../components/DndEntity';
import {
  IS_SCREENSHOT_MODE,
  MAX_COMPOUND_COHORT_SIZE,
} from '../../../../config/constants';
import { getDndActiveItemData } from '../../../../components/DndEntity/utils';
import { isEmptyEventConditions } from '../../../../utils/builder';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { ModalSliceType } from '../../../../redux/slices/modal';
import {
  IRowMeta,
  populateNamedBuilderCohort,
  RowType,
} from '../../../../redux/slices/oldBuilder';
import { cohortSampleSaveRowMeta } from '../../../../components/Builder/Cohorts/CohortRow/utils';
import useBuilderSelector from '../../../../redux/selectorHooks/useBuilderSelector';
import CohortBuilderHeader from './CohortBuilderHeader';
import CohortSampleOperation from './CohortSampleOperation';
import CohortSamplingSettings from './CohortSamplingSettings';
import ExecuteCohort from './ExecuteCohort';
import LlmPrompt from '../../../../components/Builder/LlmPrompt';
import useShowCloseLlmPrompt from '../../../../components/Builder/LlmPrompt/useShowCloseLlmPrompt';

const rowType = RowType.builderCohorts;
const measuresDropzoneId = `Cohort-Builder-New-Measure-Cohort-Row-Dropzone`;
const cohortsDropzoneId = `Cohort-Builder-New-Cohort-Row-Dropzone`;
const eventsDropzoneId = `Cohort-Builder-New-Event-Cohort-Row-Dropzone`;

const CohortBuilder = () => {
  const [autoFocusOperation, setAutoFocusOperation] = useState(false);

  const dispatch = useAppDispatch();
  const builderSampledCohort = useBuilderSelector('builderSampledCohort');
  const builderCohorts = useBuilderSelector('builderCohorts');
  const { activeItem } = useDndEntityInternalContext();
  const { showLlmPrompt, handleCloseLlmPrompt } = useShowCloseLlmPrompt(true);
  const isSyncToBrazeModalOpen = useAppSelector((state) =>
    selectOpenedModalType(
      state,
      ModalSliceType.CreateSchedule,
      builderSampledCohort?.id,
    ),
  );
  const isSaveCohortModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.SaveCohort, ''),
  );

  const handleAddSimpleCohort = ({ rowIndex }: IRowMeta) => {
    setAutoFocusOperation(rowIndex === 0);
  };

  const handleSaveCohortComplete = (cohort: ICohortFieldsFragment) => {
    if (cohort.__typename !== 'CompoundCohort') {
      dispatch(
        populateNamedBuilderCohort({
          cohort,
          rowMeta: cohortSampleSaveRowMeta,
        }),
      );
    }
  };

  const canDrop = useCallback(
    (typeCheck: (typename: string) => boolean) => {
      if (!activeItem) {
        return false;
      }
      if (builderCohorts.length >= MAX_COMPOUND_COHORT_SIZE) {
        return false;
      }

      const data = getDndActiveItemData(activeItem);
      if (!data) {
        return false;
      }

      const allEventConditionsFilled = builderCohorts.every(
        (cohort) =>
          !isEmptyEventConditions(cohort.eventConditions) ||
          cohort.analysisId ||
          cohort.cohortId ||
          cohort.measureId,
      );

      if (!allEventConditionsFilled) {
        return false;
      }

      return typeCheck(data.__typename);
    },
    [builderCohorts, activeItem],
  );

  const canDropMeasure = canDrop(
    (typename: string) =>
      typename === 'TagLeafSimpleMeasure' ||
      typename === 'TagLeafCompoundMeasure',
  );

  const canDropCohort = canDrop(
    (typename: string) => typename === 'TagLeafSimpleCohort',
  );

  const canDropEvents = canDrop(
    (typename: string) =>
      typename === 'TagLeafBasicEvent' || typename === 'TagLeafVirtualEvent',
  );

  const canAddNewCohortRow =
    !builderSampledCohort?.sampleOperation ||
    (builderSampledCohort?.sampleOperation?.key ===
      CohortSampleOperationKey.And &&
      builderCohorts.length < MAX_COMPOUND_COHORT_SIZE);

  const isDropzoneActive = canDropMeasure || canDropCohort || canDropEvents;

  const showExecuteButton = !IS_SCREENSHOT_MODE;

  return (
    <>
      <CohortBuilderHeader />
      {showLlmPrompt && (
        <LlmPrompt
          queryType={QueryType.CohortSample}
          onClose={handleCloseLlmPrompt}
        />
      )}
      <BuilderUI.Part1>
        {builderCohorts.map((cohortRow, rowIndex) => (
          <Fragment key={`builder-cohort-row-${rowIndex}`}>
            {rowIndex > 0 && (
              <CohortSampleOperation autoFocus={autoFocusOperation} />
            )}
            <CohortRow
              rowMeta={{
                rowType,
                rowIndex,
              }}
              cohortRow={cohortRow}
              allowRemove={builderCohorts.length > 1}
            />
          </Fragment>
        ))}
        {canAddNewCohortRow && !isDropzoneActive && (
          <BuilderAddRowAction
            rowMeta={{
              rowType,
              rowIndex: builderCohorts.length - 1,
            }}
            onClick={handleAddSimpleCohort}
            label={'+ Add Cohort'}
            trackContext="builder"
            trackAction="add cohort"
          />
        )}
        {canDropMeasure && (
          <DndDroppableZone
            accepts={[EntityDropzoneType.Measures]}
            id={measuresDropzoneId}
            data={
              {
                id: measuresDropzoneId,
                type: EntityDropzoneDataTypeEnum.Measures,
                rowMeta: {
                  rowType,
                  rowIndex: builderCohorts.length,
                },
                addCohortRow: true,
              } satisfies IMeasureEntityDropzoneDataType
            }
          >
            <DropzoneArea size="large">+ Add Measure as Cohort</DropzoneArea>
          </DndDroppableZone>
        )}
        {canDropCohort && (
          <DndDroppableZone
            accepts={[EntityDropzoneType.Cohorts]}
            id={cohortsDropzoneId}
            data={
              {
                id: cohortsDropzoneId,
                type: EntityDropzoneDataTypeEnum.Cohorts,
                rowMeta: {
                  rowType,
                  rowIndex: builderCohorts.length,
                },
                addCohortRow: true,
              } satisfies ICohortEntityDropzoneDataType
            }
          >
            <DropzoneArea size="large">+ Add Cohort</DropzoneArea>
          </DndDroppableZone>
        )}
        {canDropEvents && (
          <DndDroppableZone
            accepts={[EntityDropzoneType.Events]}
            id={eventsDropzoneId}
            data={
              {
                id: eventsDropzoneId,
                type: EntityDropzoneDataTypeEnum.Events,
                rowMeta: {
                  rowType,
                  rowIndex: builderCohorts.length,
                  eventIndex: 0,
                },
                addCohortRow: true,
              } satisfies IEventEntityDropzoneDataType
            }
          >
            <DropzoneArea size="large">+ Add Event as New Segment</DropzoneArea>
          </DndDroppableZone>
        )}
        {builderSampledCohort?.sampleOperation?.key ===
          CohortSampleOperationKey.Compare && (
          <>
            <BuilderUI.HorizontalDivider />
            <ReroutingEvents
              allowExpand={true}
              allowMerge={true}
              allowFold={false}
            />
          </>
        )}
      </BuilderUI.Part1>
      <BuilderUI.Part2>
        <CohortSamplingSettings />
      </BuilderUI.Part2>
      {showExecuteButton && <ExecuteCohort />}
      {isSyncToBrazeModalOpen && (
        <Scheduler
          entityId={builderSampledCohort.id}
          jobType={ScheduleType.BrazeCohortImport}
          prefilledName={builderSampledCohort.name}
          isStaticDateRangeCohort={true}
          ignoreModalData={true}
        />
      )}
      {isSaveCohortModalOpen && (
        <SaveCohortModal onSubmit={handleSaveCohortComplete} />
      )}
    </>
  );
};

export default CohortBuilder;
